import type { Content, TableLayout } from 'pdfmake/interfaces';
import type { ViewBlock } from '@/data/schemas';
import { pdfColors, tableLayouts } from '../../theme.pdf';
import { formatPdfCellValue, resolvePdfAlignment, resolvePdfColumnWidth } from '../formatters';
import { BLOCK_SPACING } from '../layout';

type TableBlock = Extract<ViewBlock, { type: 'table' }>;

type LayoutKey = keyof typeof tableLayouts;

export function renderTableBlock(
  block: TableBlock,
  options: { layout?: LayoutKey; marginBottom?: number } = {}
): Content {
  const layoutKey = options.layout ?? 'default';
  const marginBottom = options.marginBottom ?? BLOCK_SPACING;

  if (block.rows.length === 0) {
    return {
      text: block.emptyMessage ?? 'Nessun dato disponibile',
      style: 'bodyMuted',
      italics: true,
      margin: [0, 0, 0, marginBottom],
    };
  }

  const headerCells = block.columns.map((col) => ({
    text: col.label,
    style: 'tableHeader',
    bold: true,
    alignment: resolvePdfAlignment(col.align),
  }));

  return {
    table: {
      headerRows: 1,
      keepWithHeaderRows: 1,
      dontBreakRows: true,
      widths: block.columns.map((col) => resolvePdfColumnWidth(col.width)),
      body: [
        headerCells,
        ...block.rows.map((row) =>
          block.columns.map((col) => ({
            text: formatPdfCellValue(row.values[col.id]),
            style: 'tableCell',
            alignment: resolvePdfAlignment(col.align),
            fillColor: row.highlight ? pdfColors.yellow : undefined,
          }))
        ),
      ],
    },
    layout: tableLayouts[layoutKey] as TableLayout,
    margin: [0, 0, 0, marginBottom],
  };
}
